import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { InfoIcon, LockIcon, PenLineIcon, UserPlusIcon } from 'lucide-react';
import { ListPageLayout } from '../components/lists/ListPageLayout';
import { LogCommunicationModal } from '../components/communication/LogCommunicationModal';
import { AddRealtorDrawer } from '../components/realtors/AddRealtorDrawer';
import { useToast } from '../contexts/ToastContext';
import { realtors } from '../data/realtors';
import type { CommunicationSubject, Realtor } from '../types/portal';
const filters = [{
  id: '30',
  label: 'Last 30 days'
}, {
  id: '60',
  label: 'Last 60 days'
}, {
  id: '90',
  label: 'Last 90 days'
}, {
  id: 'due',
  label: 'Follow-up due'
}, {
  id: 'all',
  label: 'All partners'
}];
function isDue(realtor: Realtor) {
  return realtor.daysSinceMyTouch !== null && realtor.daysSinceMyTouch >= 7;
}
export function MyRealtorsPage() {
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('90');
  const [subject, setSubject] = useState<CommunicationSubject | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 600);
    return () => window.clearTimeout(timer);
  }, []);
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return realtors.filter(realtor => {
      const days = realtor.daysSinceMyTouch;
      const matchesFilter = filter === 'all' ? true : filter === 'due' ? isDue(realtor) : days !== null && days <= Number(filter);
      const matchesSearch = term.length === 0 || realtor.name.toLowerCase().includes(term) || realtor.company.toLowerCase().includes(term) || realtor.market.toLowerCase().includes(term);
      return matchesFilter && matchesSearch;
    });
  }, [filter, search]);
  const filterChips = filters.map(item => ({
    ...item,
    count: item.id === 'all' ? realtors.length : item.id === 'due' ? realtors.filter(isDue).length : realtors.filter(realtor => realtor.daysSinceMyTouch !== null && realtor.daysSinceMyTouch <= Number(item.id)).length
  }));
  return <>
      <ListPageLayout title="My Realtors" subtitle="Realtor, title, and insurance partners you’ve logged communication with." actions={<button type="button" onClick={() => setAddOpen(true)} className="inline-flex h-11 items-center gap-2 rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-751" data-mp="2mt2fn1yotmop">
            <UserPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-752" data-mp="30qr40jryrbyi" />
            Add partner
          </button>} banner={<div className="flex items-start gap-3 rounded-2xl border border-brand-200 bg-brand-50 p-4" data-id="element-753" data-mp="35j21lfz8elnw">
            <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" data-id="element-754" data-mp="u0nopb4yv5u4" />
            <p className="text-sm text-brand-900" data-id="element-755" data-mp="12uu02bbcqle6">
              Realtors are shared across the company to prevent duplicates. This
              list is built from your logged communication — keep touching a
              partner and they stay on your list. House accounts are locked to
              the Reach in-house team.
            </p>
          </div>} searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search all partners by name, brokerage, or market" filters={filterChips} activeFilter={filter} onFilterChange={setFilter} loading={loading} isEmpty={visible.length === 0} emptyTitle="No partners in this window" emptyMessage="Log a call, text, or visit against a partner and they’ll show up here. Use Add partner to search the shared database." resultLabel={`Showing ${visible.length} of ${realtors.length} partners`} data-id="element-750" data-mp="2hrwkkxhk1nc0">
        <ul className="divide-y divide-navy-100" data-id="element-756" data-mp="2qufvjcs7kmfo">
          {visible.map(realtor => {
          const locked = realtor.access === 'house-locked';
          const due = isDue(realtor);
          return <li key={realtor.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-757" data-mp="30teshy3m2tmu">
                <div className="min-w-0" data-id="element-758" data-mp="39rql25o1g1hx">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-759" data-mp="39qffc0b18jo6">
                    <Link to={`/realtors/${realtor.id}`} className="text-sm font-semibold text-navy-900 hover:text-brand-700" data-id="element-760" data-mp="3ek0hm4jnrzt6">
                      {realtor.name}
                    </Link>
                    <span className="inline-flex items-center rounded-full bg-navy-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-navy-600 ring-1 ring-navy-200" data-id="element-761" data-mp="3sj02r27ypwkl">
                      {realtor.partnerType}
                    </span>
                    {locked ? <span className="inline-flex items-center gap-1 rounded-full bg-navy-800 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white" data-id="element-762" data-mp="17uzud75q026a">
                        <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-763" data-mp="12y9g8pdh7gni" />
                        House account
                      </span> : null}
                    {due ? <span className="inline-flex items-center rounded-full bg-warn-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-warn-700 ring-1 ring-warn-200" data-id="element-764" data-mp="1vmx5sx59cs6v">
                        Follow-up due
                      </span> : null}
                  </div>
                  <p className="mt-1 text-sm text-navy-700" data-id="element-765" data-mp="2jchmtahupfdi">
                    {realtor.company} · {realtor.market}
                  </p>
                  <p className="mt-1 text-xs text-navy-500" data-id="element-766" data-mp="clw8wykpyiy1">
                    {realtor.lastTouchLabel ?? 'No communication logged by you'}{' '}
                    · {realtor.sharedBuyers} shared buyers ·{' '}
                    {realtor.referralsYtd} referrals YTD
                    {realtor.otherOfficersActive > 0 ? ` · ${realtor.otherOfficersActive} other officer${realtor.otherOfficersActive === 1 ? '' : 's'} active` : ''}
                  </p>
                </div>

                <div className="flex shrink-0 gap-2" data-id="element-767" data-mp="3u3fewdpdgja8">
                  {locked ? <p className="max-w-[16rem] text-xs font-medium text-navy-500" data-id="element-768" data-mp="3g3wlys54teii">
                      Managed by the Reach house team — view only.
                    </p> : <button type="button" onClick={() => setSubject({
                id: realtor.id,
                name: realtor.name,
                context: `${realtor.company} · ${realtor.partnerType}`,
                phone: realtor.phone,
                email: realtor.email,
                goalCredit: realtor.partnerType === 'Realtor' ? 'your Realtor touches goal' : 'your title & insurance partner touches goal'
              })} className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 sm:flex-none" data-id="element-769" data-mp="1ecjh2335z6mg">
                      <PenLineIcon className="h-4 w-4" aria-hidden="true" data-id="element-770" data-mp="2h55itmjfoiro" />
                      Log touch
                    </button>}
                  <Link to={`/realtors/${realtor.id}`} className="inline-flex h-11 min-w-[5.5rem] items-center justify-center rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-771" data-mp="mq2wtytlfiti">
                    View
                  </Link>
                </div>
              </li>;
        })}
        </ul>
      </ListPageLayout>

      <LogCommunicationModal subject={subject} onClose={() => setSubject(null)} onLogged={showToast} data-id="element-772" data-mp="1p2o0u7mb99eu" />
      <AddRealtorDrawer open={addOpen} onClose={() => setAddOpen(false)} onToast={showToast} data-id="element-773" data-mp="sdq6urmdxfiq" />
    </>;
}